import { getConfigForImportedTest, getConfigForMeasuredTest } from 'Components/Worksheets/Config';
import { IConfigFunctionArgs } from 'Components/Worksheets/Core';
import {
  wrapAsDerivedFromMeasurementsConfig as derivedFrom,
  measuredUsingConfigFor as measuredUsing,
  mergeConfig,
} from 'Components/Worksheets/Core/Helpers/configHelpers';
import { DryMassSource, WetMassSource } from 'Components/Worksheets/PsdSieveAnalysisWorksheet/types';
import qestIds from 'Components/Worksheets/QestIds';
import * as oPath from 'Lib/Object/Path/Immutable';
import getLabelConfig from './labelConfig';

import { getSeparationPath } from 'Components/Worksheets/PsdSieveAnalysisWorksheet/Helpers';
import { getDerivedFromMeasurementsConfigForPortionSieving } from 'Components/Worksheets/PsdSieveAnalysisWorksheet/derivedFromMeasurementsConfig';
import { getMeasuredUsingConfigForPortionSieving } from 'Components/Worksheets/PsdSieveAnalysisWorksheet/measuredUsingConfig';
import {
  IQuantity,
  isGreaterThanOrEqualTo as isIQuantityGreaterThanOrEqualTo,
  isLessThan as isIQuantityLessThan,
  toIQuantity,
} from 'Data/ViewModels/Core/IQuantity';
/**
 * Most masses are formatted based on the readability of the corresponding balance.
 * However, some require more complicated logic to determine which balances apply.
 * For these masses, we're currently keeping it simple and displaying them to a fixed 0.1 g
 * (default readability).
 */
const massConfig = { uom: 'g', format: { nearest: 0.1 } };

const getConfig = (prefix: string) => {
  const maxSeparations = 3;
  let config = {
    [`${prefix}.Preparation.NominalParticleSize`]: {
      uom: 'mm',
      format: { nearest: 0.1 },
    },
    [`${prefix}.Preparation.AddSieveSizesBasedOnSpecification`]: {
      options: [
        { value: false, label: 'Based on the selected sieve set' },
        { value: true, label: `Based on the sample's specification` },
      ],
    },
    ...getPortionMassesConfig(`${prefix}.WholePortion.Masses`),
    ...getPortionSievingConfig(prefix, `${prefix}.WholePortion`),
    ...getPortionWashingConfig(`${prefix}.WholePortion.Washing`),
    ...getConfigForImportedTest(
      qestIds.binderContent.AS_2891_3_2.rev2013,
      `${prefix}.WholePortion.Masses.AssociatedBinderOrBitumenContentTest`,
      [qestIds.bitumenContent.AS_2891_3_1, qestIds.bitumenContent.AS_2891_3_3]
    ),
    [`${prefix}.WholePortion.Masses.AssociatedBinderOrBitumenContentTest.ExternalTest.Results.AggregateMass`]: (
      configArgs: IConfigFunctionArgs
    ) => getMassReadability(oPath.get(configArgs.worksheet, `${prefix}.WholePortion.Masses.DryMass`) as IQuantity),

    [`${prefix}.Results.D85`]: { format: { nearest: 0.0001 } },
    [`${prefix}.Results.D60`]: { format: { nearest: 0.0001 } },
    [`${prefix}.Results.D50`]: { format: { nearest: 0.0001 } },
    [`${prefix}.Results.D30`]: { format: { nearest: 0.0001 } },
    [`${prefix}.Results.D15`]: { format: { nearest: 0.0001 } },
    [`${prefix}.Results.D10`]: { format: { nearest: 0.0001 } },
    [`${prefix}.Results.CoefficientOfCurvature`]: { format: { nearest: 0.01 } },
    [`${prefix}.Results.CoefficientOfUniformity`]: { format: { nearest: 0.01 } },
    [`${prefix}.Results.FinenessModulus`]: { format: { nearest: 0.01 } },
    [`${prefix}.ImagesAndRemarks.Remarks`]: { maxLength: 255 },
    [`${prefix}.ImagesAndRemarks.InternalRemarks`]: { maxLength: 255 },
  };

  Object.assign(
    config,
    ...[...Array(maxSeparations).keys()].map((i) => ({
      ...getPortionMassesConfig(`${prefix}.${getSeparationPath(i)}.CoarsePortion.Masses`),
      ...getPortionSievingConfig(prefix, `${prefix}.${getSeparationPath(i)}.CoarsePortion`),
      ...getPortionMassesConfig(`${prefix}.${getSeparationPath(i)}.FinePortion.Masses`),
      //For this test method, the "fine portion moisture content" is attached to the fine portion subspecimen,
      //but it is actually a *different* subspecimen. We avoid the 'Subspecimen' prefix because we don't want
      //users to be confused by it.
      ...getPortionMassesConfig(`${prefix}.${getSeparationPath(i)}.FinePortion.Subspecimen.Masses`),
      ...getPortionSievingConfig(prefix, `${prefix}.${getSeparationPath(i)}.FinePortion.Subspecimen`),
    }))
  );

  return mergeConfig(config, getLabelConfig(prefix));
};

const getPortionMassesConfig = (prefix: string) => ({
  [`${prefix}.DryMass`]: (configArgs: IConfigFunctionArgs) =>
    getMassReadability(oPath.get(configArgs.worksheet, `${prefix}.DryMass`) as IQuantity),
  [`${prefix}.MassUpscaleFactor`]: { format: { nearest: 0.01 } },

  ...getPortionMassMeasurementConfig(prefix),
});

const getPortionWashingConfig = (prefix: string) => {
  const measuredUsingDryMassBalance = measuredUsing(
    `${prefix}.AssociatedWashTest.MeasuredTest.Masses.MeasuredWashedConstantDryMass.Equipment.DryMassBalance`
  );

  return mergeConfig({
    [`${prefix}.WashedDryMass`]: measuredUsingDryMassBalance,
    ...getConfigForMeasuredTest(qestIds.wash.AS_1141_11_1.rev2020, `${prefix}.AssociatedWashTest`),
  });
};

const getSieveConfig = (portionSievingPath: string) => (configArgs: any) => {
  const units = oPath.get(configArgs.worksheet, `${portionSievingPath}.Equipment.SieveSetUnits`);
  return {
    format: units === 'Metric' ? { nearest: 1 } : { nearest: 0.1 },
    uom: units === 'Metric' ? 'mm' : 'in',
  };
};

//Note that the prefix parameter should already contain the root prefix within, we just needed the root separately
const getPortionSievingConfig = (rootPrefix: string, portionPath: string) => {
  const portionSievingPath = `${portionPath}.Sieving`;
  return mergeConfig(
    {
      [`${portionSievingPath}.Equipment.SieveSetDimensions.Width`]: getSieveConfig(portionSievingPath),
      [`${portionSievingPath}.Equipment.SieveSetDimensions.Length`]: getSieveConfig(portionSievingPath),
      [`${portionSievingPath}.Equipment.SieveSetDimensions.Diameter`]: getSieveConfig(portionSievingPath),
      [`${portionSievingPath}.RepresentedDryMass`]: massConfig,
      [`${portionSievingPath}.Records.PercentagePassing`]: { format: { nearest: 0.1 } },
      [`${portionSievingPath}.Records.MaximumIndividualMassRetained`]: massConfig,
      [`${portionSievingPath}.Records.PercentagePassing`]: { format: { nearest: 0.1 } },
      [`${portionSievingPath}.Records.PercentageRetained`]: { format: { nearest: 0.1 } },
      [`${portionSievingPath}.Records.IndividualPercentageRetained`]: { format: { nearest: 0.1 } },
      [`${portionSievingPath}.ExpectedIndividualPanMassRetained`]: (configArgs: IConfigFunctionArgs) =>
        getMassReadability(
          oPath.get(configArgs.worksheet, `${portionSievingPath}.ExpectedIndividualPanMassRetained`) as IQuantity
        ),
    },
    getMeasuredUsingConfigForPortionSieving(portionSievingPath),
    getDerivedFromMeasurementsConfigForPortionSieving(portionSievingPath),
    {
      [`${portionSievingPath}.Equipment.Balance`]: (configArgs: IConfigFunctionArgs) =>
        getDefaultReadability(oPath.get(configArgs.worksheet, `${portionPath}.Masses.DryMass`) as IQuantity),
    }
  );
};

const getPortionMassMeasurementConfig = (portionPrefix: string) => {
  const prefix = `${portionPrefix}.MassMeasurement`;
  const dryMassOnly = (configArgs: IConfigFunctionArgs) => {
    const wetMassSource = oPath.get(configArgs.worksheet, `${portionPrefix}.WetMassSource`) as WetMassSource;
    const dryMassSource = oPath.get(
      configArgs.worksheet,
      `${portionPrefix}.StateAfterMassMeasurement`
    ) as DryMassSource;
    return wetMassSource !== WetMassSource.Measured && dryMassSource !== DryMassSource.Measured;
  };

  const measuredUsingBalance = measuredUsing(`${prefix}.Equipment.Balance`);
  const measuredUsingDryMassBalance = measuredUsing(`${prefix}.Equipment.DryMassBalance`);

  return {
    ...getMeasuredConstantDryMassConfig(`${prefix}`),
    [`${prefix}.TareID`]: { maxLength: 20 },
    [`${prefix}.TareMass`]: (configArgs: IConfigFunctionArgs) =>
      dryMassOnly(configArgs) ? measuredUsingDryMassBalance : measuredUsingBalance,
    [`${prefix}.TareAndWetMass`]: measuredUsingBalance,
    [`${prefix}.TareAndDryMass`]: measuredUsingDryMassBalance,

    [`${prefix}.WetMass`]: derivedFrom([`${prefix}.TareMass`, `${prefix}.TareAndWetMass`]),
    [`${prefix}.DryMass`]: derivedFrom([`${prefix}.TareMass`, `${prefix}.TareAndDryMass`]),

    [`${prefix}.Equipment.DryMassBalance`]: (configArgs: IConfigFunctionArgs) =>
      getDefaultReadability(oPath.get(configArgs.worksheet, `${prefix}.DryMass`) as IQuantity),
  };
};

const getMeasuredConstantDryMassConfig = (portionMassMeasurementPrefix: string) => {
  const prefix = `${portionMassMeasurementPrefix}.MeasuredConstantDryMass`;

  const measuredUsingBalance = measuredUsing(`${prefix}.Equipment.Balance`);
  const measuredUsingDryMassBalance = measuredUsing(`${prefix}.Equipment.DryMassBalance`);

  return {
    [`${prefix}.Equipment.DryingTemperature`]: { uom: '°C', format: { nearest: 0.1 } },

    [`${prefix}.DryingTray.ContainerMass`]: measuredUsingBalance,
    [`${prefix}.DryingTray.WetSoilAndContainerMass`]: measuredUsingBalance,
    [`${prefix}.DryingTray.WetSoilMass`]: derivedFrom([
      `${prefix}.DryingTray.ContainerMass`,
      `${prefix}.DryingTray.WetSoilAndContainerMass`,
    ]),

    [`${prefix}.DryingTray.DryMassReadings.DrySoilAndContainerMass`]: measuredUsingDryMassBalance,
    [`${prefix}.DryingTray.DryMassReadings.DryingTime`]: {
      uom: 'h',
      format: { nearest: 0.01 },
    },

    [`${prefix}.Measurements.TotalDryMass`]: derivedFrom([
      `${prefix}.DryingTray.ContainerMass`,
      `${prefix}.DryingTray.DryMassReadings.DrySoilAndContainerMass`,
    ]),

    [`${prefix}.Equipment.Balance`]: (configArgs: IConfigFunctionArgs) =>
      getDefaultReadability(oPath.get(configArgs.worksheet, `${prefix}.Measurements.TotalWetMass`) as IQuantity),

    [`${prefix}.Equipment.DryMassBalance`]: (configArgs: IConfigFunctionArgs) =>
      getDefaultReadability(oPath.get(configArgs.worksheet, `${prefix}.Measurements.TotalWetMass`) as IQuantity),
  };
};

const getDefaultReadability = (sampleMass: IQuantity) => {
  //default readability: 0.1 when sampleMass >= 100 and sampleMass < 500
  let defaultReadability = 0.1;

  if (isIQuantityLessThan(sampleMass, toIQuantity(100, 'g'))) {
    defaultReadability = 0.01;
  } else if (isIQuantityGreaterThanOrEqualTo(sampleMass, toIQuantity(500, 'g'))) {
    defaultReadability = 1;
  }

  return {
    defaultReadability,
    defaultUom: 'g',
  };
};

const getMassReadability = (sampleMass: IQuantity) => {
  //default readability: 0.1 when sampleMass >= 100 and sampleMass < 500
  let nearest = 1;

  if (isIQuantityLessThan(sampleMass, toIQuantity(1000, 'g'))) {
    nearest = 0.1;
  }

  return {
    uom: 'g',
    format: { nearest: nearest },
  };
};

export { getConfig };

export default {
  [qestIds.psdSieveAnalysis.AS_1141_11_1.rev2020]: getConfig,
};
